type ReportCustomerNameAndBusinessKeyInput = {
  name?: string | null;
  businessKey?: string | null;
};

type ReportCustomerNameAndBusinessKey = {
  name: string | undefined;
  businessKey: string | undefined;
};

type ReportPdfFileNameOptions = {
  customerNameAndBusinessKey: ReportCustomerNameAndBusinessKey;
  reportKind: 'portfolio' | 'building';
  /** Building address slug; included only for building reports */
  address?: string;
  /** Defaults to true. Building downloads via PdfService should pass false (it appends .pdf). */
  includeExtension?: boolean;
};

function normalizeForBusinessKeyComparison(value: string): string {
  return value.trim().replace(/^0+/, '');
}

function trimToUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Maps the customer name and optional business key for report cover/footer.
 * When the name equals the business key (ignoring leading zeros), the business key is
 * suppressed so it is not shown twice.
 */
export function mapReportCustomerNameAndBusinessKey(
  customer: ReportCustomerNameAndBusinessKeyInput | undefined,
  options: { showBusinessKeyInFooter: boolean }
): ReportCustomerNameAndBusinessKey {
  if (!customer) {
    return { name: undefined, businessKey: undefined };
  }

  const name = trimToUndefined(customer.name) ?? trimToUndefined(customer.businessKey);
  const businessKey = trimToUndefined(customer.businessKey);

  if (!options.showBusinessKeyInFooter || !businessKey) {
    return { name, businessKey: undefined };
  }

  if (name && normalizeForBusinessKeyComparison(name) === businessKey) {
    return { name, businessKey: undefined };
  }

  return { name, businessKey };
}

/**
 * Builds the text prefix for report footers (date is still formatted in the template).
 * Examples: "Zone 16 BVBA - ", "ZONE… - 1145277911 - ", "" when no customer name.
 */
export function formatReportFooterText(customer: ReportCustomerNameAndBusinessKey): string {
  if (!customer.name) {
    return '';
  }

  if (customer.businessKey) {
    return `${customer.name} - ${customer.businessKey} - `;
  }

  return `${customer.name} - `;
}

function sanitizeReportFileNamePart(value: string): string {
  return value
    .trim()
    .replace(/,/g, '')
    .replace(/\s+/g, '-')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Builds a report PDF file name using the same customer name / business key rules as the footer.
 * Portfolio: `{name}-{businessKey?}-portfolio-report.pdf`
 * Building: `{name}-{businessKey?}-{address?}-building-report.pdf`
 */
export function buildReportPdfFileName(options: ReportPdfFileNameOptions): string {
  const parts: string[] = [];
  const { customerNameAndBusinessKey } = options;

  if (customerNameAndBusinessKey.name) {
    parts.push(sanitizeReportFileNamePart(customerNameAndBusinessKey.name));
  }

  if (customerNameAndBusinessKey.businessKey) {
    parts.push(sanitizeReportFileNamePart(customerNameAndBusinessKey.businessKey));
  }

  if (options.reportKind === 'building' && options.address) {
    const sanitizedAddress = sanitizeReportFileNamePart(options.address);
    if (sanitizedAddress) {
      parts.push(sanitizedAddress);
    }
  }

  parts.push(`${options.reportKind}-report`);

  const fileName = parts.filter(Boolean).join('-');
  const includeExtension = options.includeExtension !== false;
  return includeExtension ? `${fileName}.pdf` : fileName;
}

export function formatBuildingAddressFileNamePart(addressDisplay: string[] | undefined): string {
  if (!addressDisplay?.length) {
    return '';
  }
  return addressDisplay.join('-').replace(/,/g, '').replace(/ /g, '-');
}
